import {
  InputLabel,
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material'

import { Page } from 'Common/Page/Page'
import { Input, PageHeader, DropDown, Button } from 'Common/Supplyvue-ui'
import { useEffect, useMemo, useState } from 'react'
import {
  useCreateScenario,
  useCreateScenarioConfig,
  useProjectData,
} from './Scenarios.data'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader } from 'Common/Loader/Loader'
import dayjs, { Dayjs } from 'dayjs'
import { DatePickerMUI } from './Calender'
import { FormValuesType } from './Scenarios.types'
import { toast } from 'react-hot-toast'

export const HISTORICAL = 'Historical'
export const FORECAST = 'Forecast'

type MenuItem = {
  value: string | number
  label: string
}
const salesRateTypeDropDownValues: MenuItem[] = [
  {
    value: HISTORICAL,
    label: 'Historical',
  },
  {
    value: FORECAST,
    label: 'Forecast',
  },
]

const templateSelectionDropDownValues: MenuItem[] = [
  {
    value: 0,
    label: 'New template',
  },
  {
    value: 1,
    label: 'System of records',
  },
  {
    value: 2,
    label: 'Nudged template',
  },
]

const formValues: FormValuesType = {
  scenarioName: '',
  salesRatePeriodType: HISTORICAL,
  periodStartWeek: dayjs(),
  numberOfWeeks: 1,
  template: 0,
}

type Config = {
  maxDate: Dayjs
  minDate: Dayjs
  numWeeks: number
}
const config: Config = {
  maxDate: dayjs(),
  minDate: dayjs(),
  numWeeks: 0,
}

export const CreateScenarioPage = () => {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()
  const [formData, setFormData] = useState(formValues)
  const [configData, setConfigData] = useState(config)

  const { data, isLoading } = useCreateScenarioConfig(
    projectId as string,
    formData.salesRatePeriodType
  )

  const { data: projectData } = useProjectData(projectId)

  useEffect(() => {
    if (data) {
      const maxDate = dayjs(data.maxDate)
      const minDate = dayjs(data.minDate)
      setConfigData({
        maxDate: maxDate,
        minDate: minDate,
        numWeeks: data.numWeeks,
      })
      setFormData({
        ...formData,
        periodStartWeek: minDate,
      })
    }
  }, [data])

  const weekSelectionDropDown = useMemo(() => {
    const weeks = []
    const startWeek = formData.periodStartWeek
    const endweek = configData.maxDate
    const diff = endweek.diff(startWeek, 'week')
    for (let i = 1; i <= diff; i++) {
      weeks.push({
        value: i,
        label: `${i} weeks`,
      })
    }
    return weeks
  }, [formData.periodStartWeek, configData.maxDate])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }
  const handleDropDownChange = (name: string, value: string): void => {
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleCalendarInputChange = (name: string, value: Dayjs) => {
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const onScenarioCreateSuccess = () => {
    // Handle success logic here
    toast.success('Scenario created successfully')
    // Redirect to the scenarios page or show a success message
    navigate(`/projects/${projectId}`)
  }
  const onScenarioCreateFailure = () => {
    // Handle failure logic here
    toast.error('Scenario creation failed')
  }
  const { mutate: createScenario, isLoading: isCreating } = useCreateScenario(
    onScenarioCreateSuccess,
    onScenarioCreateFailure
  )

  const handleCreateButtonClick = () => {
    // Handle button click logic here
    const sequenceId = projectData?.scenariosCount ?? 0
    const data = {
      ...formData,
      projectId: projectId as string,
      sequenceId: sequenceId,
    }
    createScenario(data)
  }
  const handleCancelButtonClick = () => {
    setFormData(formValues)
  }

  const shouldDisableDate = (date: Dayjs) => {
    return date.day() !== 1
  }

  const isCreateButtonDisabled = () => {
    for (const key in formData) {
      if (formData[key as keyof FormValuesType] === '') {
        return true
      }
    }
    return false
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <Page>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
      >
        <PageHeader title="Create scenario" />
        <Box
          aria-label="Create scenario form container"
          width="50%"
          height="600px"
        >
          <Paper sx={{ padding: 3 }}>
            {/* Create a form with out using form element. */}

            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              General
            </Typography>
            {/* Scenario name */}
            <Box
              display={'flex'}
              flexDirection="row"
              mt={1}
              alignItems="center"
              mb={2}
            >
              <Box mr={2} width="300px" ml={1}>
                <InputLabel htmlFor="scenario-name">Scenario name</InputLabel>
              </Box>
              <Box width="300px">
                <Input
                  fullWidth
                  type="text"
                  id="scenario-name"
                  name="scenarioName"
                  placeholder="Scenario name"
                  aria-label="Scenario name"
                  value={formData.scenarioName}
                  onChange={handleInputChange}
                />
              </Box>
            </Box>
            {/* Scenario name ends*/}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              Sales rate
            </Typography>
            {/* Sales rate period type */}
            <Box
              display={'flex'}
              flexDirection="row"
              alignItems="center"
              mb={2}
            >
              <Box mr={2} width="300px" ml={1}>
                <InputLabel htmlFor="scenario-name">
                  Sales rate period type
                </InputLabel>
              </Box>
              <Box width="300px">
                <DropDown
                  key="sales-rate-period-type"
                  name="sales-rate-period-type"
                  menuItems={salesRateTypeDropDownValues}
                  label="Sales rate period type"
                  defaultValue={HISTORICAL}
                  onChange={(event) =>
                    handleDropDownChange(
                      'salesRatePeriodType',
                      event?.target?.value as string
                    )
                  }
                  value={formData.salesRatePeriodType}
                />
              </Box>
            </Box>

            {/* Sales rate period ends */}

            {/* Period start week */}

            <Box
              display={'flex'}
              flexDirection="row"
              alignItems="center"
              mb={2}
            >
              <Box mr={2} width="300px" ml={1}>
                <InputLabel htmlFor="scenario-name">
                  Period start week
                </InputLabel>
              </Box>
              <Box width="300px">
                <DatePickerMUI
                  sx={{
                    width: '100%',
                  }}
                  label="Period start week"
                  value={formData.periodStartWeek}
                  onChange={(value: Dayjs) =>
                    handleCalendarInputChange('periodStartWeek', value)
                  }
                  format="DD-MM-YYYY"
                  minDate={configData.minDate}
                  maxDate={configData.maxDate}
                  shouldDisableDate={shouldDisableDate}
                />
              </Box>
            </Box>
            {/* Period start week ends */}

            {/* Number of weeks */}
            <Box
              display={'flex'}
              flexDirection="row"
              alignItems="center"
              mb={2}
            >
              <Box mr={2} width="300px" ml={1}>
                <InputLabel htmlFor="scenario-name">Number of weeks</InputLabel>
              </Box>
              <Box width="300px">
                <DropDown
                  key="number-of-weeks"
                  name="number-of-weeks"
                  menuItems={weekSelectionDropDown}
                  label="Number of weeks"
                  defaultValue={1}
                  onChange={(event) =>
                    handleDropDownChange(
                      'numberOfWeeks',
                      event?.target?.value as string
                    )
                  }
                  value={formData.numberOfWeeks}
                />
              </Box>
            </Box>
            {/* Number of weeks ends */}

            {/* Template selection */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              Template
            </Typography>
            <Box display={'flex'} flexDirection="row" alignItems="center">
              <Box mr={2} width="300px" ml={1}>
                <InputLabel htmlFor="scenario-name">
                  Template selection
                </InputLabel>
              </Box>
              <Box width="300px">
                <DropDown
                  key="template-selection"
                  name="template"
                  menuItems={templateSelectionDropDownValues}
                  label="Template selection"
                  defaultValue={0}
                  onChange={(event) =>
                    handleDropDownChange(
                      'template',
                      event?.target?.value as string
                    )
                  }
                  value={formData.template}
                />
              </Box>
            </Box>
            {/* Template selection ends */}

            {/* Error message */}
            {/* Button Part */}
            <Box
              display={'flex'}
              flexDirection="row"
              alignItems="center"
              mt={2}
            >
              <Box mr={2} width="300px" ml={1}></Box>
              <Box width="300px" display="flex">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancelButtonClick}
                >
                  Cancel
                </Button>
                <Box ml={2}></Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateButtonClick}
                  disabled={isCreateButtonDisabled() || isCreating}
                >
                  {isCreating && (
                    <CircularProgress
                      color="inherit"
                      size={16}
                      sx={{ marginRight: 1 }}
                    />
                  )}
                  {'Create Scenario'}
                </Button>
              </Box>
            </Box>
            {/* Button Part End */}
          </Paper>
        </Box>
      </Box>
    </Page>
  )
}
